import { REGULAR_NODE_CAPTION_CLASS_NAME, REGULAR_NODE_TYPE_CLASS_NAME } from "Common/componentUtils/constants";
import getShorterText from "Common/componentUtils/getShorterText";
import { documentObjectIconMap } from "Common/dictionaries/documentObjectIconId";
import { themeVariables } from "Components/theme";
import classNames from "classnames";
import toString from "lodash/toString";
import React, { useMemo } from "react";
import UnPinIcon from "./unPinIcon/unPinIcon";
import getNodeImage from "./utils/getNodeImage";
import getNodeName from "./utils/getNodeName";
import styles from "./nodeLabel.module.css";
const SIZES = {
    SMALL_DOCUMENT_OBJECT_ICON_SIZE: 14,
    SHOW_SMALL_DOCUMENT_OBJECT_ICON_WHEN_BIG_ICON_IS_NOT_LESS_THAN: 40
};
const mainDataContainerStyle = {
    display: "grid",
    alignContent: "center",
    overflow: "hidden"
};
const smallDocumentObjectIconStyle = {
    width: SIZES.SMALL_DOCUMENT_OBJECT_ICON_SIZE,
    height: SIZES.SMALL_DOCUMENT_OBJECT_ICON_SIZE,
    borderRadius: "50%",
    objectFit: "cover",
    display: "block"
};
const smallDocumentObjectIconContainerStyle = {
    width: SIZES.SMALL_DOCUMENT_OBJECT_ICON_SIZE + 4,
    height: SIZES.SMALL_DOCUMENT_OBJECT_ICON_SIZE + 4,
    display: "grid",
    alignContent: "center",
    justifyContent: "center",
    background: "#fff",
    border: `1px solid ${themeVariables["gray-100"]}`,
    borderRadius: "50%",
    position: "absolute",
    bottom: -2,
    right: -2
};
export default function NodeLabel(props) {
    var _a, _b, _c, _d;
    const labelRootStyle = useMemo(() => ({
        ...(props.style || {}),
        display: "grid",
        gridTemplateColumns: props.imgSize && props.showName ? "auto 1fr" : "1fr",
        gridTemplateRows: "1fr",
        boxSizing: "border-box",
        position: "relative"
    }), [props.style, Boolean(props.imgSize), Boolean(props.showName)]);
    const displayNameStyle = useMemo(() => ({
        padding: "1px 0",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: props.whiteSpace,
        wordBreak: "break-word"
    }), [props.showDocumentObjectName, props.whiteSpace]);
    const documentObject = useMemo(() => { var _a; return ((_a = props.data) === null || _a === void 0 ? void 0 : _a.DocumentObject) || props.documentObject; }, [(_a = props.data) === null || _a === void 0 ? void 0 : _a.DocumentObject, props.documentObject]);
    const displayName = ((_b = props.data) === null || _b === void 0 ? void 0 : _b.NodeName) || getNodeName((_c = props.data) === null || _c === void 0 ? void 0 : _c.AttributeValues, documentObject);
    const documentObjectNameStyle = useMemo(() => ({
        fontSize: displayName ? undefined : "inherit",
        whiteSpace: props.whiteSpace,
        overflow: "hidden",
        textOverflow: "ellipsis",
        paddingTop: displayName ? 2 : 0
    }), [(props.whiteSpace, displayName)]);
    const userImgSrc = useMemo(() => props.data
        ? props.data.NodeImage || getNodeImage(props.data.AttributeValues, documentObject)
        : undefined, [props.data, documentObject]);
    const imageContainerStyle = useMemo(() => props.imgSize
        ? {
            width: props.imgSize,
            height: props.imgSize,
            minHeight: props.imgSize,
            minWidth: props.imgSize,
            position: "relative",
            marginRight: 10,
            opacity: props.imgDefaultGrayscale && !userImgSrc ? 0.2 : undefined
        }
        : undefined, [props.imgSize, props.imgDefaultGrayscale, userImgSrc]);
    const imgStyle = useMemo(() => props.imgSize
        ? {
            filter: props.imgDefaultGrayscale && !userImgSrc ? "grayscale(1)" : undefined,
            borderRadius: "50%",
            objectFit: "cover",
            border: "0"
        }
        : undefined, [props.imgDefaultGrayscale, userImgSrc]);
    if (!props.data)
        return null;
    const documentObjectIcon = (documentObject === null || documentObject === void 0 ? void 0 : documentObject.IconId)
        ? documentObjectIconMap[documentObject.IconId]
        : undefined;
    const resultImgSrc = Boolean(props.imgSize) && (userImgSrc || documentObjectIcon);
    const showSmallDocumentObjectIcon = userImgSrc &&
        documentObjectIcon &&
        props.imgSize >= SIZES.SHOW_SMALL_DOCUMENT_OBJECT_ICON_WHEN_BIG_ICON_IS_NOT_LESS_THAN;
    return (React.createElement("div", { className: classNames("node-label", props.data.Color && "node-label-colored", props.className), style: labelRootStyle, ...(props.attrs || {}) },
        resultImgSrc && (React.createElement("div", { style: imageContainerStyle },
            React.createElement("img", { src: resultImgSrc, width: props.imgSize, height: props.imgSize, title: "", alt: "", style: imgStyle }),
            showSmallDocumentObjectIcon && (React.createElement("div", { style: smallDocumentObjectIconContainerStyle },
                React.createElement("img", { src: documentObjectIcon, width: props.imgSize, height: props.imgSize, title: "", style: smallDocumentObjectIconStyle }))))),
        props.showName && (React.createElement("div", { style: mainDataContainerStyle },
            displayName && (React.createElement("div", { style: displayNameStyle, title: toString(displayName), className: classNames(styles.title, REGULAR_NODE_CAPTION_CLASS_NAME) }, getShorterText(displayName, props.titleMaxLength))),
            (props.showDocumentObjectName || !displayName) && (React.createElement("div", { style: documentObjectNameStyle, className: classNames(styles["sub-title"], REGULAR_NODE_TYPE_CLASS_NAME) }, (_d = props.documentObject) === null || _d === void 0 ? void 0 : _d.Name)))),
        props.onUnPin && props.data && (React.createElement(UnPinIcon, { data: props.data, onUnPin: props.onUnPin, zIndex: props.zIndex }))));
}
NodeLabel.defaultProps = {
    imgSize: 50,
    titleMaxLength: 90,
    whiteSpace: "pre-wrap",
    showName: true
};
